import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { companyAPI, platformAPI, fileUrl } from '../../services/api';
import toast from 'react-hot-toast';
import { User, Lock, ArrowRight, Shield, Building2, BookOpen, PieChart, CreditCard, Phone, MessageSquare, RefreshCw } from 'lucide-react';
import { firebaseEnabled, sendPhoneOtp, confirmPhoneOtp, signInWithGoogle } from '../../services/firebase';

/* Google "G" logo */
export const GoogleIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.4 17.7 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/>
    <path fill="#FBBC05" d="M10.4 28.7a14.5 14.5 0 0 1 0-9.4l-7.8-6.1a23.9 23.9 0 0 0 0 21.6l7.8-6.1z"/>
    <path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.4-5.5l-7.5-5.8c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.7-3.9-13.6-9.3l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/>
  </svg>
);

/**
 * Detect if a user is a platform super admin.
 * Super admin = user id=1 OR no company_id with Administrator/Superadmin role.
 */
function isSuperAdmin(user) {
  if (!user) return false;
  // If user has a company_id, they are a COMPANY user, never a platform superadmin
  if (user.company_id) return false;
  // No company_id: platform superadmin if id=1 (seeded owner) or admin role
  if (user.id === 1) return true;
  if (['Administrator', 'Superadmin'].includes(user.role_name)) return true;
  return false;
}

export default function Login() {
  const { login, loginWithPhone, loginWithGoogle, logout } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detectedRole, setDetectedRole] = useState(null); // 'superadmin' | 'company' | null
  const [companyLogo, setCompanyLogo] = useState(null);
  const [platformLogo, setPlatformLogo] = useState(null);

  // Phone OTP state
  const [mode, setMode] = useState('password'); // 'password' | 'phone'
  const [phone, setPhone] = useState('+93');
  const [otpCode, setOtpCode] = useState('');
  const [otpStage, setOtpStage] = useState('phone'); // 'phone' | 'code'
  const [otpBusy, setOtpBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [confirmResult, setConfirmResult] = useState(null);

  useEffect(() => {
    platformAPI.getPublicSettings().then(data => {
      if (data.logo_path) setPlatformLogo(data.logo_path);
    }).catch(() => {});

    // Show why the user was signed out (no subscription / expired trial)
    const lockReason = sessionStorage.getItem('ledgerpro_lock_reason');
    if (lockReason) {
      sessionStorage.removeItem('ledgerpro_lock_reason');
      setTimeout(() => toast.error(lockReason, { duration: 6000 }), 300);
    }
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  /** Shared post-login handling for both password and phone flows */
  const finishLogin = async (data) => {
    const user = data.user;

    // Companies without an active plan or unexpired trial are logged out immediately
    if (user.company_id && data.access_state && data.access_state !== 'ok') {
      logout();
      toast.error(data.message || 'Your subscription is inactive. Please choose a plan to continue.', { duration: 6000 });
      return;
    }

    // Redirect based on detected role
    if (isSuperAdmin(user)) {
      toast.success('Platform Admin detected — redirecting to Admin Portal');
      setTimeout(() => navigate('/super-admin'), 500);
    } else {
      toast.success(`Welcome back, ${user.first_name || user.username}!`);
      setTimeout(() => navigate('/dashboard'), 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDetectedRole(null);
    setCompanyLogo(null);
    try {
      const data = await login(username, password);
      await finishLogin(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Phone OTP flow ----------
  const handleSendOtp = async () => {
    if (!/^\+[1-9]\d{7,14}$/.test(phone.trim())) {
      toast.error('Enter a valid phone number in international format, e.g. +93700123456');
      return;
    }
    setOtpBusy(true);
    try {
      const confirmation = await sendPhoneOtp(phone.trim(), 'otp-recaptcha');
      setConfirmResult(confirmation);
      setOtpStage('code');
      setResendIn(45);
      toast.success(`Verification code sent to ${phone.trim()}`);
    } catch (err) {
      toast.error(err.message?.replace('Firebase:', '').trim() || 'Could not send the code. Please try again.');
    } finally {
      setOtpBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!/^\d{6}$/.test(otpCode.trim())) {
      toast.error('Enter the 6-digit code from the SMS');
      return;
    }
    setOtpBusy(true);
    try {
      const { phoneNumber, uid, idToken } = await confirmPhoneOtp(confirmResult, otpCode.trim());
      const data = await loginWithPhone({ phone: phoneNumber, uid, idToken });
      await finishLogin(data);
    } catch (err) {
      toast.error(err.message || 'Verification failed');
    } finally {
      setOtpBusy(false);
    }
  };

  // ---------- Google sign-in ----------
  const handleGoogleSignIn = async () => {
    try {
      const g = await signInWithGoogle();
      if (!g?.email) throw new Error('Google account returned no email');
      const data = await loginWithGoogle({ email: g.email, uid: g.uid, idToken: g.idToken });
      await finishLogin(data);
    } catch (err) {
      if (err.message === 'NO_ACCOUNT') {
        toast.error('No LedgerPro account uses this Google email yet. Create one below.', { duration: 5000 });
        navigate('/register');
      } else {
        toast.error(err.message || 'Google sign-in failed');
      }
    }
  };

  return (
    <div className="auth-layout">
      {/* ========== LEFT — PREMIUM GRADIENT BRANDING PANEL ========== */}
      <div className="auth-brand">
        {/* Animated floating shapes */}
        <div className="auth-brand-shapes">
          <div className="auth-shape auth-shape-1" />
          <div className="auth-shape auth-shape-2" />
          <div className="auth-shape auth-shape-3" />
          <div className="auth-shape auth-shape-4" />
          <div className="auth-shape auth-shape-5" />
        </div>

        <div className="auth-brand-content">
          {/* Logo */}
          <div className="auth-brand-logo">
            {platformLogo ? (
              <img src={fileUrl(platformLogo)} alt="Logo" style={{ width: 52, height: 52, borderRadius: 14, objectFit: 'contain', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }} />
            ) : (
              <div className="auth-brand-logo-icon">
                <BookOpen size={28} />
              </div>
            )}
            <div>
              <div className="auth-brand-logo-text">LedgerPro</div>
              <div className="auth-brand-logo-sub">Professional Accounting</div>
            </div>
          </div>

          {/* Headline */}
          <div className="auth-brand-headline">
            <h2>
              Manage Your Finances<br />
              <span className="accent">With Confidence</span>
            </h2>
            <p>
              Streamline your accounting, invoicing, and financial reporting
              with an all-in-one platform built for modern businesses.
            </p>
          </div>

          {/* Feature highlights */}
          <div className="auth-brand-features">
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon teal">
                <PieChart size={20} />
              </div>
              <div className="auth-brand-feature-text">
                <h4>Real-time Dashboard</h4>
                <p>Track revenue, expenses, and cash flow at a glance</p>
              </div>
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon blue">
                <CreditCard size={20} />
              </div>
              <div className="auth-brand-feature-text">
                <h4>Invoicing & Payments</h4>
                <p>Create, send, and track invoices effortlessly</p>
              </div>
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon purple">
                <BookOpen size={20} />
              </div>
              <div className="auth-brand-feature-text">
                <h4>Double-Entry Bookkeeping</h4>
                <p>Professional-grade journal entries and ledgers</p>
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="auth-brand-trust">
            <div className="auth-trust-item">
              <Shield size={16} />
              Bank-level Security
            </div>
            <div className="auth-trust-item">
              <Building2 size={16} />
              Multi-company Support
            </div>
          </div>
        </div>
      </div>

      {/* ========== RIGHT — FORM PANEL ========== */}
      <div className="auth-form-panel">
        <div className="auth-form-wrapper">
          <div className="auth-card">
            <div className="auth-card-header">
              <div className="auth-form-logo">
                {companyLogo ? (
                  <img src={companyLogo} alt="Company Logo" className="auth-form-logo-img" style={{ objectFit: 'contain' }} />
                ) : platformLogo ? (
                  <img src={fileUrl(platformLogo)} alt="Platform Logo" className="auth-form-logo-img" style={{ objectFit: 'contain' }} />
                ) : (
                  <img src="/favicon.svg" alt="LedgerPro" className="auth-form-logo-img" />
                )}
              </div>
              <h2>Welcome back</h2>
              <p>Sign in to your LedgerPro account</p>
            </div>
            <div className="auth-card-body">
              {/* Sign-in mode switch */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 18, background: '#f1f5f9', padding: 4, borderRadius: 10 }}>
                <button type="button" onClick={() => setMode('password')}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer', background: mode === 'password' ? '#fff' : 'transparent', color: mode === 'password' ? '#0f172a' : '#64748b', boxShadow: mode === 'password' ? '0 1px 3px rgba(15,23,42,.12)' : 'none' }}>
                  <Lock size={13} /> Password
                </button>
                <button type="button" onClick={() => setMode('phone')} disabled={!firebaseEnabled} title={!firebaseEnabled ? 'SMS login is not configured yet' : undefined}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', cursor: firebaseEnabled ? 'pointer' : 'not-allowed', opacity: firebaseEnabled ? 1 : 0.45, background: mode === 'phone' ? '#fff' : 'transparent', color: mode === 'phone' ? '#0f172a' : '#64748b', boxShadow: mode === 'phone' ? '0 1px 3px rgba(15,23,42,.12)' : 'none' }}>
                  <Phone size={13} /> Phone OTP
                </button>
              </div>

              {mode === 'password' ? (
                <form onSubmit={handleSubmit}>
                <div className="auth-field">
                  <label>Username or Email</label>
                  <div className="auth-input-icon">
                    <User size={18} className="auth-input-icon-svg" />
                    <input
                      className="auth-input"
                      type="text"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      required
                      placeholder="Enter your username"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="auth-field">
                  <label>Password</label>
                  <div className="auth-input-icon auth-input-with-toggle">
                    <Lock size={18} className="auth-input-icon-svg" />
                    <input
                      className="auth-input"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      placeholder="Enter your password"
                    />
                  </div>
                </div>
                <div className="auth-field-row">
                  <label className="auth-checkbox-label">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)}
                    />
                    <span>Remember me</span>
                  </label>
                  <Link to="/forgot-password" className="auth-forgot-link">
                    Forgot password?
                  </Link>
                </div>
                <button className="auth-submit auth-submit-primary" type="submit" disabled={loading || !!detectedRole}>
                  {loading ? (
                    <>
                      <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                      Signing in...
                    </>
                  ) : detectedRole === 'superadmin' ? (
                    <>
                      <Shield size={16} />
                      Redirecting to Admin Portal...
                    </>
                  ) : detectedRole === 'company' ? (
                    <>
                      <Building2 size={16} />
                      Redirecting to Dashboard...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>

                {/* Role detection indicator */}
                {detectedRole && (
                  <div className="auth-role-indicator" style={{
                    marginTop: 12,
                    padding: '10px 14px',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    background: detectedRole === 'superadmin' ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${detectedRole === 'superadmin' ? '#fecaca' : '#bbf7d0'}`,
                    color: detectedRole === 'superadmin' ? '#991b1b' : '#166534',
                  }}>
                    {detectedRole === 'superadmin' ? (
                      <><Shield size={16} /> Platform Admin — Redirecting to Admin Portal</>
                    ) : (
                      <><Building2 size={16} /> Company User — Redirecting to Dashboard</>
                    )}
                  </div>
                )}
                </form>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); otpStage === 'phone' ? handleSendOtp() : handleVerifyOtp(); }}>
                  {otpStage === 'phone' ? (
                    <>
                      <div className="auth-field">
                        <label>Phone Number</label>
                        <div className="auth-input-icon">
                          <Phone size={18} className="auth-input-icon-svg" />
                          <input
                            className="auth-input"
                            type="tel"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            placeholder="+93700123456"
                            autoFocus
                          />
                        </div>
                        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 5 }}>International format with country code, e.g. +93700123456</div>
                      </div>
                      <button className="auth-submit auth-submit-primary" type="submit" disabled={otpBusy}>
                        {otpBusy ? (
                          <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Sending code...</>
                        ) : (
                          <><MessageSquare size={16} /> Send verification code</>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="auth-field">
                        <label>Verification code sent to <b>{phone}</b></label>
                        <input
                          className="auth-input"
                          inputMode="numeric"
                          maxLength={6}
                          value={otpCode}
                          onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="● ● ● ● ● ●"
                          style={{ textAlign: 'center', letterSpacing: 8, fontSize: 20, fontWeight: 700 }}
                          autoFocus
                        />
                      </div>
                      <button className="auth-submit auth-submit-primary" type="submit" disabled={otpBusy}>
                        {otpBusy ? (
                          <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Verifying...</>
                        ) : (
                          <>Verify & Sign In <ArrowRight size={16} /></>
                        )}
                      </button>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 12.5 }}>
                        <button type="button" onClick={() => { setOtpStage('phone'); setOtpCode(''); setConfirmResult(null); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>
                          ← Change number
                        </button>
                        <button type="button" onClick={handleSendOtp} disabled={resendIn > 0 || otpBusy} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: resendIn > 0 ? '#94a3b8' : '#4f46e5', cursor: resendIn > 0 ? 'default' : 'pointer', fontWeight: 600 }}>
                          <RefreshCw size={12} /> {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                        </button>
                      </div>
                    </>
                  )}
                </form>
              )}

              {/* Google sign-in */}
              <div className="auth-divider"><span>or</span></div>
              <button type="button" onClick={handleGoogleSignIn}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '11px 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>
                <GoogleIcon size={17} /> Sign in with Google
              </button>

              <div className="auth-divider">
                <span>New to LedgerPro?</span>
              </div>

              <Link to="/register" style={{ textDecoration: 'none' }}>
                <button className="auth-submit" type="button" style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}>
                  Create Your Company
                </button>
              </Link>
            </div>
          </div>

          <div className="auth-footer">
            <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>
              © 2026 LedgerPro. Professional Accounting System.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 6 }}>
              <a href="/privacy-policy" style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'none' }}>Privacy Policy</a>
              <span style={{ color: '#d1d5db' }}>·</span>
              <a href="/terms-of-service" style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'none' }}>Terms of Service</a>
              <span style={{ color: '#d1d5db' }}>·</span>
              <a href="/security" style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'none' }}>Security</a>
              <span style={{ color: '#d1d5db' }}>·</span>
              <a href="/plans" style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'none' }}>View Plans</a>
              <span style={{ color: '#d1d5db' }}>·</span>
              <a href="/contact" style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'none' }}>Contact</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
